import { useEffect, useRef } from 'react';

const products = [
  {
    id: 'p1',
    name: 'Metro Chain',
    tagline: 'Built for movement. No noise.',
    type: 'Necklace',
    featured: false,
    image:
      'https://public.readdy.ai/ai/img_res/498d69952f1d1ff2ddb45e1414d65438.jpg',
  },
  {
    id: 'p2',
    name: 'Silent Ring',
    tagline: 'Minimal. Unseen. Intentional.',
    type: 'Ring',
    featured: true,
    image:
      'https://readdy.ai/api/search-image?query=sleek%20minimalist%20silver%20ring%20on%20dark%20black%20satin%20fabric%20close%20up%20macro%20shot%20dramatic%20cinematic%20lighting%20sharp%20metallic%20reflection%20luxury%20jewelry%20editorial%20photography%20noir%20aesthetic%20high%20contrast&width=600&height=760&seq=ub006&orientation=portrait',
  },
  {
    id: 'p3',
    name: 'Pulse Bracelet',
    tagline: 'Subtle presence. Constant rhythm.',
    type: 'Bracelet',
    featured: false,
    image:
      'https://readdy.ai/api/search-image?query=chrome%20silver%20luxury%20bracelet%20draped%20on%20dark%20surface%20dramatic%20side%20lighting%20metallic%20texture%20editorial%20jewelry%20fashion%20photography%20minimalist%20dark%20mood%20noir%20high%20contrast%20deep%20shadows%20product%20shot&width=600&height=760&seq=ub007&orientation=portrait',
  },
  {
    id: 'p4',
    name: 'Night Pendant',
    tagline: 'Worn in silence. Seen in moments.',
    type: 'Pendant',
    featured: false,
    image:
      'https://readdy.ai/api/search-image?query=dark%20luxury%20pendant%20necklace%20silver%20gold%20charm%20hanging%20on%20black%20background%20dramatic%20studio%20lighting%20metallic%20shine%20editorial%20jewelry%20photography%20high%20contrast%20noir%20minimalist%20fashion%20accessory%20deep%20shadows%20sharp%20focus&width=600&height=760&seq=ub008&orientation=portrait',
  },
];

export default function FirstDropSection() {
  const headerRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLParagraphElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.06 }
    );
    if (headerRef.current) observer.observe(headerRef.current);
    if (introRef.current) observer.observe(introRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);
    cardRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="section-first-drop"
      data-section="First Drop"
      className="w-full py-24 md:py-36 overflow-hidden"
      style={{ background: '#080808', borderTop: '1px solid rgba(255,255,255,0.06)' }}
    >
      <div className="w-full px-8 md:px-16">

        {/* ── Header ── */}
        <div
          ref={headerRef}
          className="reveal mb-6 md:mb-10"
        >
          <span className="block font-display text-[9px] tracking-[0.5em] uppercase text-white/30 mb-5">
            Dropping Soon
          </span>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 md:gap-0">
            <div>
              <h2
                className="font-display font-black uppercase text-white leading-none"
                style={{ fontSize: 'clamp(36px, 6vw, 72px)', letterSpacing: '-0.025em' }}
              >
                First Drop
              </h2>
              <p
                className="mt-3 font-display text-xs tracking-[0.35em] uppercase text-white/35"
              >
                A limited release.&nbsp;&nbsp;No repeats.
              </p>
            </div>

            {/* Numeric accent */}
            <span
              className="font-display font-black text-white/06 select-none hidden md:block"
              style={{ fontSize: '110px', lineHeight: 1, letterSpacing: '-0.05em', color: 'rgba(255,255,255,0.04)' }}
              aria-hidden="true"
            >
              01
            </span>
          </div>
        </div>

        {/* ── Micro Intro ── */}
        <p
          ref={introRef}
          className="reveal reveal-delay-1 font-display text-sm text-white/40 leading-loose tracking-wide mb-14 md:mb-20 max-w-md"
          style={{ lineHeight: '2' }}
        >
          Built for those who move without noise.<br />
          Designed to be worn, not noticed.
        </p>

        {/* ── Products grid ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 items-start">
          {products.map((product, i) => (
            <div
              key={product.id}
              ref={(el) => { cardRefs.current[i] = el; }}
              className={`reveal reveal-delay-${i + 1} group relative cursor-pointer`}
            >
              {/* Image container — featured ring gets a subtle gold ring outline */}
              <div
                className="relative overflow-hidden"
                style={{
                  aspectRatio: product.featured ? '3/4.2' : '3/4',
                  boxShadow: product.featured
                    ? '0 0 0 1px rgba(212,175,55,0.28), 0 0 32px 0 rgba(212,175,55,0.08)'
                    : 'none',
                }}
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-[1.05] transition-transform duration-1000"
                />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all duration-500" />

                {/* Featured glow pulse (silent ring only) */}
                {product.featured && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'radial-gradient(ellipse at 50% 80%, rgba(212,175,55,0.08) 0%, transparent 65%)',
                    }}
                  />
                )}

                {/* Badge */}
                {product.featured ? (
                  <div className="absolute top-4 left-4">
                    <span
                      className="font-display text-[8px] tracking-[0.35em] uppercase px-3 py-1.5"
                      style={{
                        background: 'linear-gradient(135deg, #C0C0C0, #D4AF37)',
                        color: '#000',
                      }}
                    >
                      Featured
                    </span>
                  </div>
                ) : i === 0 ? (
                  <div className="absolute top-4 left-4">
                    <span
                      className="font-display text-[8px] tracking-[0.3em] uppercase px-3 py-1 border"
                      style={{ borderColor: 'rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.5)' }}
                    >
                      Limited Release
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Product info */}
              <div className="pt-5 pb-3">
                <span className="block font-display text-[8px] tracking-[0.4em] uppercase text-white/25 mb-1.5">
                  {product.type}
                </span>
                <h3
                  className={`font-display font-bold uppercase tracking-wide ${
                    product.featured ? 'text-white text-sm' : 'text-white/80 text-xs'
                  }`}
                >
                  {product.name}
                </h3>
                <p className="mt-1.5 font-display text-[10px] text-white/35 leading-relaxed tracking-wide">
                  {product.tagline}
                </p>
                <div
                  className="mt-3 w-0 group-hover:w-full h-px transition-all duration-500"
                  style={{ background: 'linear-gradient(90deg, #C0C0C0, #D4AF37)' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* ── Thin divider ── */}
        <div className="mt-20 md:mt-28 mb-14 md:mb-16">
          <div
            className="w-full h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)' }}
          />
        </div>

        {/* ── CTA Block ── */}
        <div
          ref={ctaRef}
          className="reveal flex flex-col items-center text-center gap-6"
        >
          <p className="font-display text-white/45 text-xs tracking-[0.3em] uppercase leading-loose">
            Get access before the drop goes live.<br />
            <span className="text-white/25">Limited units. No restocks.</span>
          </p>

          <a
            href="#early-access"
            className="inline-flex items-center gap-3 font-display font-bold text-[11px] tracking-[0.35em] uppercase text-black px-12 py-5 transition-all duration-300 hover:opacity-85 whitespace-nowrap cursor-pointer"
            style={{ background: 'linear-gradient(135deg, #D4D4D4 0%, #F0F0F0 40%, #C8C8C8 70%, #D4AF37 100%)' }}
          >
            Enter Early Access
            <span className="text-[10px] opacity-60">&#8594;</span>
          </a>
        </div>

      </div>
    </section>
  );
}
